import { get } from "lodash";
import toast from "react-hot-toast";
import { z } from "zod";
import moment from "moment";
import { Link, useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useIsMobile } from "routes/customerAccount/hooks/useIsMobile";
import { NATIONALITY_OPTIONS } from "utills/constants";
import {
  DetailCustomerAccountType,
  FavoriteEventType,
} from "routes/customerAccount/types";
import { EyeIcon, PencilIcon } from "@heroicons/react/outline";
import { CollapsibleSection } from "components/collapsibleSection";
import { Label } from "components/label";
import Input from "components/input";
import { SwitchButton } from "components/switchButton";
import Pagination from "components/pagination";
import Button from "components/button";

interface PropType {
  data: DetailCustomerAccountType;
  mode: "create" | "edit" | "detail";
  handleChangePage: (page: number) => void;
}

const CustomerAccountForm = (props: PropType) => {
  const { data, mode, handleChangePage } = props;

  const PAGE_SIZE = 10;

  const navigate = useNavigate();
  const isMobile = useIsMobile(1024);

  const FormSchema = z.object({
    id: z.number(),
    code: z.string(),
    full_name: z.string().nullable(),
    email: z.string(),
    phone: z.string(),
    phone_area_code: z.string(),
    last_login_at: z.string(),
    created_at: z.string(),
    updated_at: z.string(),
    created_by: z.string(),

    first_name: z.string(),
    last_name: z.string(),
    gender: z.string().nullable(),
    birthday: z.string().nullable(),
    nationality: z.string().nullable(),
    id_card: z.string().nullable(),
    is_active: z.boolean(),
    is_sent_activation: z.boolean(),
    sent_activation_at: z.string(),
  });

  type FormData = z.infer<typeof FormSchema>;

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    setFocus,
    setValue,
    watch,
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(FormSchema),
    defaultValues: data ?? {
      id: 0,
      code: "",
      full_name: null,
      email: "",
      phone: "",
      phone_area_code: "",
      last_login_at: "",
      created_at: "",
      updated_at: "",
      created_by: "",

      first_name: "",
      last_name: "",
      gender: null,
      birthday: null,
      nationality: null,
      id_card: null,
      is_active: false,
      is_sent_activation: false,
      sent_activation_at: "",
    },
  });

  const handleformatDate = (value: string) => {
    const m = moment(value, "YYYY-MM-DD HH:mm:ss", true); // strict
    return m.isValid() ? m.format("DD-MM-YYYY HH:mm:ss") : "";
  };

  const onSubmit = async (data: FormData) => {
    console.log(data);
  };

  const onError = (errors: any) => {
    console.error("Submit failed:", errors);

    const firstErrorKey = Object.keys(errors)[0];
    setFocus(firstErrorKey as any);
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onError)}
      className="w-full flex flex-col gap-y-4 h-full sm:gap-y-6"
    >
      <CollapsibleSection label={"Profile"} defaultOpen={true}>
        <section className="p-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
          <Controller
            control={control}
            name="is_active"
            render={({ field, fieldState }) => (
              <div className={`flex flex-col gap-1`}>
                <Label required={false} id="Status" name="Status" />

                <SwitchButton
                  checked={field.value}
                  disabled={mode === "detail"}
                  onChange={() => {
                    setValue("is_active", !field.value);
                  }}
                />

                {fieldState.error && (
                  <span className="error-text font-medium">
                    {fieldState.error.message}
                  </span>
                )}
              </div>
            )}
          />

          <Controller
            control={control}
            name="code"
            render={({ field, fieldState }) => (
              <div className={`flex flex-col gap-1`}>
                <Label
                  required={false}
                  id="Customer Code"
                  name="Customer Code"
                />

                {mode === "detail" ? (
                  <span className="text-[14px] font-[400] text-[#000]">
                    {field.value}
                  </span>
                ) : (
                  <Input
                    {...field}
                    type="text"
                    error={fieldState.error}
                    placeholder="Customer Code"
                    className="bg-white"
                  />
                )}

                {fieldState.error && (
                  <span className="error-text font-medium">
                    {fieldState.error.message}
                  </span>
                )}
              </div>
            )}
          />

          <Controller
            control={control}
            name="full_name"
            render={({ field, fieldState }) => (
              <div className={`flex flex-col gap-1`}>
                <Label required={false} id="full_name" name="Full Name" />

                {mode === "detail" ? (
                  <span className="text-[14px] font-[400] text-[#000]">
                    {field.value}
                  </span>
                ) : (
                  <Input
                    {...field}
                    type="text"
                    error={fieldState.error}
                    placeholder="Full Name"
                    className="bg-white"
                  />
                )}

                {fieldState.error && (
                  <span className="error-text font-medium">
                    {fieldState.error.message}
                  </span>
                )}
              </div>
            )}
          />

          <Controller
            control={control}
            name="last_name"
            render={({ field, fieldState }) => (
              <div className={`flex flex-col gap-1`}>
                <Label required={false} id="last_name" name="Last Name" />

                {mode === "detail" ? (
                  <span className="text-[14px] font-[400] text-[#000]">
                    {field.value}
                  </span>
                ) : (
                  <Input
                    {...field}
                    type="text"
                    error={fieldState.error}
                    placeholder="Last Name"
                    className="bg-white"
                  />
                )}

                {fieldState.error && (
                  <span className="error-text font-medium">
                    {fieldState.error.message}
                  </span>
                )}
              </div>
            )}
          />

          <Controller
            control={control}
            name="first_name"
            render={({ field, fieldState }) => (
              <div className={`flex flex-col gap-1`}>
                <Label required={false} id="first_name" name="First Name" />

                {mode === "detail" ? (
                  <span className="text-[14px] font-[400] text-[#000]">
                    {field.value}
                  </span>
                ) : (
                  <Input
                    {...field}
                    type="text"
                    error={fieldState.error}
                    placeholder="First Name"
                    className="bg-white"
                  />
                )}

                {fieldState.error && (
                  <span className="error-text font-medium">
                    {fieldState.error.message}
                  </span>
                )}
              </div>
            )}
          />

          <Controller
            control={control}
            name="email"
            render={({ field, fieldState }) => (
              <div className={`flex flex-col gap-1`}>
                <Label required={false} id="email" name="Email" />

                {mode === "detail" ? (
                  <span className="text-[14px] font-[400] text-[#000]">
                    {field.value}
                  </span>
                ) : (
                  <Input
                    {...field}
                    type="text"
                    error={fieldState.error}
                    placeholder="Email"
                    className="bg-white"
                  />
                )}

                {fieldState.error && (
                  <span className="error-text font-medium">
                    {fieldState.error.message}
                  </span>
                )}
              </div>
            )}
          />

          <Controller
            control={control}
            name="gender"
            render={({ field, fieldState }) => (
              <div className={`flex flex-col gap-1`}>
                <Label required={false} id="Gender" name="Gender" />

                {mode === "detail" ? (
                  <span className="text-[14px] font-[400] text-[#000]">
                    {field.value}
                  </span>
                ) : (
                  <Input
                    {...field}
                    type="text"
                    error={fieldState.error}
                    placeholder="Gender"
                    className="bg-white"
                  />
                )}

                {fieldState.error && (
                  <span className="error-text font-medium">
                    {fieldState.error.message}
                  </span>
                )}
              </div>
            )}
          />

          <Controller
            control={control}
            name="id_card"
            render={({ field, fieldState }) => (
              <div className={`flex flex-col gap-1`}>
                <Label required={false} id="ID Card" name="ID Card" />

                {mode === "detail" ? (
                  <span className="text-[14px] font-[400] text-[#000]">
                    {field.value}
                  </span>
                ) : (
                  <Input
                    {...field}
                    type="text"
                    error={fieldState.error}
                    placeholder="ID Card"
                    className="bg-white"
                  />
                )}

                {fieldState.error && (
                  <span className="error-text font-medium">
                    {fieldState.error.message}
                  </span>
                )}
              </div>
            )}
          />

          <Controller
            control={control}
            name="phone"
            render={({ field, fieldState }) => (
              <div className={`flex flex-col gap-1`}>
                <Label required={false} id="Phone" name="Phone" />

                {mode === "detail" ? (
                  <span className="text-[14px] font-[400] text-[#000]">
                    {field.value}
                  </span>
                ) : (
                  <Input
                    {...field}
                    type="text"
                    error={fieldState.error}
                    placeholder="Phone"
                    className="bg-white"
                  />
                )}

                {fieldState.error && (
                  <span className="error-text font-medium">
                    {fieldState.error.message}
                  </span>
                )}
              </div>
            )}
          />

          <Controller
            control={control}
            name="birthday"
            render={({ field, fieldState }) => {
              const value = handleformatDate(field.value ?? "");

              return (
                <div className={`flex flex-col gap-1`}>
                  <Label required={false} id="Birthday" name="Birthday" />

                  {mode === "detail" ? (
                    <span className="text-[14px] font-[400] text-[#000]">
                      {value}
                    </span>
                  ) : (
                    <Input
                      {...field}
                      type="text"
                      value={value}
                      error={fieldState.error}
                      placeholder="Birthday"
                      className="bg-white"
                    />
                  )}

                  {fieldState.error && (
                    <span className="error-text font-medium">
                      {fieldState.error.message}
                    </span>
                  )}
                </div>
              );
            }}
          />

          <Controller
            control={control}
            name="nationality"
            render={({ field, fieldState }) => (
              <div className={`flex flex-col gap-1`}>
                <Label required={false} id="Nationality" name="Nationality" />

                {mode == "detail" ? (
                  <span className="text-[14px] font-[400] text-[#000]">
                    {
                      NATIONALITY_OPTIONS.find((option) => {
                        return option.value === field.value;
                      })?.label
                    }
                  </span>
                ) : (
                  <Input
                    {...field}
                    type="text"
                    error={fieldState.error}
                    placeholder="Nationality"
                    className="bg-white"
                  />
                )}

                {fieldState.error && (
                  <span className="error-text font-medium">
                    {fieldState.error.message}
                  </span>
                )}
              </div>
            )}
          />

          <Controller
            control={control}
            name="last_login_at"
            render={({ field, fieldState }) => {
              const value = handleformatDate(field.value ?? "");

              return (
                <div className={`flex flex-col gap-1`}>
                  <Label
                    required={false}
                    id="Last Login At"
                    name="Last Login At"
                  />

                  {mode === "detail" ? (
                    <span className="text-[14px] font-[400] text-[#000]">
                      {value}
                    </span>
                  ) : (
                    <Input
                      {...field}
                      type="text"
                      value={value}
                      error={fieldState.error}
                      placeholder="Last Login At"
                      className="bg-white"
                    />
                  )}

                  {fieldState.error && (
                    <span className="error-text font-medium">
                      {fieldState.error.message}
                    </span>
                  )}
                </div>
              );
            }}
          />

          <Controller
            control={control}
            name="is_sent_activation"
            render={({ field, fieldState }) => (
              <div className={`flex flex-col gap-1`}>
                <Label
                  required={false}
                  id="Is Sent Activation OTP Email:"
                  name="Is Sent Activation OTP Email:"
                />

                <SwitchButton
                  checked={field.value ? field.value : false}
                  disabled={mode === "detail"}
                  onChange={() => {
                    setValue("is_sent_activation", !field.value);
                  }}
                />

                {fieldState.error && (
                  <span className="error-text font-medium">
                    {fieldState.error.message}
                  </span>
                )}
              </div>
            )}
          />

          <Controller
            control={control}
            name="sent_activation_at"
            render={({ field, fieldState }) => {
              const value = handleformatDate(field.value ?? "");

              return (
                <div className={`flex flex-col gap-1`}>
                  <Label
                    required={false}
                    id="Send Activation At"
                    name="Send Activation At"
                  />

                  {mode === "detail" ? (
                    <span className="text-[14px] font-[400] text-[#000]">
                      {value}
                    </span>
                  ) : (
                    <Input
                      {...field}
                      type="text"
                      value={value}
                      error={fieldState.error}
                      placeholder="Send Activation At"
                      className="bg-white"
                    />
                  )}

                  {fieldState.error && (
                    <span className="error-text font-medium">
                      {fieldState.error.message}
                    </span>
                  )}
                </div>
              );
            }}
          />

          <Controller
            control={control}
            name="created_at"
            render={({ field, fieldState }) => {
              const value = handleformatDate(field.value ?? "");

              return (
                <div className={`flex flex-col gap-1`}>
                  <Label required={false} id="Created At" name="Created At" />

                  {mode === "detail" ? (
                    <span className="text-[14px] font-[400] text-[#000]">
                      {value}
                    </span>
                  ) : (
                    <Input
                      {...field}
                      type="text"
                      value={value}
                      error={fieldState.error}
                      placeholder="Created At"
                      className="bg-white"
                    />
                  )}

                  {fieldState.error && (
                    <span className="error-text font-medium">
                      {fieldState.error.message}
                    </span>
                  )}
                </div>
              );
            }}
          />

          <Controller
            control={control}
            name="updated_at"
            render={({ field, fieldState }) => {
              const value = handleformatDate(field.value ?? "");

              return (
                <div className={`flex flex-col gap-1`}>
                  <Label required={false} id="Updated At" name="Updated At" />

                  {mode === "detail" ? (
                    <span className="text-[14px] font-[400] text-[#000]">
                      {value}
                    </span>
                  ) : (
                    <Input
                      {...field}
                      type="text"
                      value={value}
                      error={fieldState.error}
                      placeholder="Updated At"
                      className="bg-white"
                    />
                  )}

                  {fieldState.error && (
                    <span className="error-text font-medium">
                      {fieldState.error.message}
                    </span>
                  )}
                </div>
              );
            }}
          />
        </section>
      </CollapsibleSection>

      <CollapsibleSection label={"Favorite Events"} defaultOpen={true}>
        <section className="p-6 flex flex-col gap-5">
          {isMobile ? (
            <div className="mb-6 w-full flex flex-col gap-[8px]">
              {data?.events?.data?.map(
                (item: FavoriteEventType, index: number) => {
                  return (
                    <div
                      key={index}
                      className="w-full bg-white p-[16px] rounded-[8px] shadow-md"
                    >
                      <div className="w-full flex items-center justify-between">
                        <div className="opacity-0">action</div>

                        <div className="flex items-center gap-2">
                          <Button
                            className="w-8 h-8 bg-gray-200 hover:bg-gray-100 active:bg-gray-300"
                            onClick={() => {
                              navigate(
                                `/customer-accounts/detail/${get(
                                  item,
                                  "event_id",
                                  ""
                                )}`
                              );
                            }}
                          >
                            <EyeIcon className="w-4 h-4 text-black" />
                          </Button>
                        </div>
                      </div>

                      <div className="w-full mt-[16px] flex flex-col gap-[8px]">
                        <div className="w-full flex">
                          <span className="w-[30%] text-[14px] font-[500] text-[#262626]">
                            No
                          </span>
                          <span className="w-[70%] text-[14px] font-[400] text-[#2B3033] text-right">
                            {(get(data, "event.pagning.current_page", 1) - 1) *
                              PAGE_SIZE +
                              1 +
                              index}
                          </span>
                        </div>

                        <div className="w-full flex">
                          <span className="w-[30%] text-[14px] font-[500] text-[#262626]">
                            Event ID
                          </span>
                          <span className="w-[70%] text-[14px] font-medium text-brand-500 hover:underline break-words text-right">
                            {get(item, "event_id", 0)}
                          </span>
                        </div>

                        <div className="w-full flex">
                          <span className="w-[30%] text-[14px] font-[500] text-[#262626]">
                            Event Name VI
                          </span>
                          <span className="w-[70%] text-[14px] font-[400] text-[#2B3033] text-right">
                            {get(item, "event_name_vi", "")}
                          </span>
                        </div>

                        <div className="w-full flex">
                          <span className="w-[30%] text-[14px] font-[500] text-[#262626]">
                            Event Name EN
                          </span>
                          <span className="w-[70%] text-[14px] font-[400] text-[#2B3033] text-right">
                            {get(item, "event_name_en", "")}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                }
              )}
            </div>
          ) : (
            <div className="flow-root">
              <div className="relative overflow-x-auto min-w-full">
                <table className="mb-6 w-full divide-y divide-gray-300 bg-magnolia-500 rounded-md">
                  <thead>
                    <tr>
                      <th
                        scope="col"
                        className="th py-3.5 pl-4 pr-3 sm:pl-6 lg:pl-8"
                      >
                        No
                      </th>

                      <th scope="col" className="th">
                        Event ID
                      </th>

                      <th scope="col" className="th">
                        Event Name VI
                      </th>

                      <th scope="col" className="th">
                        Event Name EN
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 bg-white">
                    {data?.events?.data?.map(
                      (item: FavoriteEventType, index: number) => {
                        return (
                          <tr key={index}>
                            <td className="td py-4 pl-4 pr-3 sm:pl-6 lg:pl-8">
                              {(get(data, "events.data.paging", 1) - 1) *
                                PAGE_SIZE +
                                1 +
                                index}
                            </td>

                            <td className="td min-w-40 break-words">
                              <span className="w-fit font-medium text-brand-500">
                                {get(item, "event_id", "")}
                              </span>
                            </td>

                            <td className="td min-w-[230px]">
                              {get(item, "event_name_vi", "")}
                            </td>

                            <td className="td min-w-[230px]">
                              {get(item, "event_name_en", "")}
                            </td>
                          </tr>
                        );
                      }
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <Pagination
            currentPage={get(data, "events.paging.current_page", 1)}
            totalPage={get(data, "events.paging.total_page", 0)}
            total={{
              totalItems: get(data, "events.paging.total_item", 0),
              total: get(data, "events.paging.total", 0),
            }}
            onChange={handleChangePage}
          />
        </section>
      </CollapsibleSection>
    </form>
  );
};

export default CustomerAccountForm;
